import React from 'react';

import { styled } from '@spazious/dollhouse-theme';
import { PaletteColors, SizingNames } from '@spazious/dollhouse-theme';

import { BaseExtendProps, createBase } from '../Base/index.js';

const mappingSize = {
  extraSmall: '23px',
  small: '32px',
  medium: '52px',
  large: '60px',
} satisfies Record<'extraSmall' | SizingNames, string>;

export type ButtonBaseElement = HTMLButtonElement;

export interface ButtonBaseProps
  extends BaseExtendProps<React.ButtonHTMLAttributes<ButtonBaseElement>> {
  color?: PaletteColors;
  disabled?: boolean;
  size?: 'extraSmall' | SizingNames;
  minWidth?: string;
}

export const ButtonBase = styled(createBase('button')).withConfig({
  shouldForwardProp: (prop) => !['color', 'disabled', 'size', 'minWidth'].includes(prop as any),
  displayName: 'Button',
})<ButtonBaseProps>`
  border-radius: var(--shape-rounded-radius);
  padding: ${({ size = 'medium' }) => (size === 'extraSmall' ? '3px' : 'var(--spacing-padding)')};
  transition: background-color 0.3s ease-in-out;
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  outline: none;
  border: none;
  margin: 0;
  min-width: ${({ size = 'medium' }) => mappingSize[size]};
  min-height: ${({ size = 'medium' }) => mappingSize[size]};
  cursor: pointer;
  align-items: center;
  justify-content: center;
  user-select: none;
  background-color: var(--spz-sys-color-surface-container);
  color: var(--spz-sys-color-on-surface);
  & svg {
    font-size: ${({ size = 'medium' }) => (size === 'extraSmall' ? '18px' : '28px')};
  }
  &:hover {
    background-color: var(--spz-sys-color-surface-container-highest);
    color: var(--spz-sys-color-on-surface);
  }
  ${({ minWidth }) => minWidth && `min-width:${minWidth};`}
  ${({ color }) =>
    color &&
    `
    background-color: var(--spz-sys-color-${color});
    color: var(--spz-sys-color-on-${color});
    &:hover {
      background-color: var(--spz-sys-color-${color});
      color: var(--spz-sys-color-on-${color});
    }
  `}
  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
    background-color: var(--spz-sys-color-surface-container-lowest);
    color: var(--spz-sys-color-inverse-on-surface);
    &:hover {
      background-color: var(--spz-sys-color-surface-container-lowest);
      color: var(--spz-sys-color-inverse-on-surface);
    }
  `}
`;
